import {$session} from "@/plugin/dot-framework-vue-1.0.8"
import creditApi from "@/api/apps/creditApi"
import {
    AI_IMAGE_OPERATIONS,
    creditPlan,
    flushPendingCreditCalls,
    isCreditShort,
    isCreditUserNo,
    parseCreditBalance,
    parseCreditEstimate,
} from "@/util/canvas/aiCredit.mjs"

/*
 AI 크레딧 잔액(user-app v2 비용 기반). 헤더의 "AI 크레딧" 메뉴와 이미지 생성/업로드 창이 같은 값을 본다.
 status: 'idle' | 'loading' | 'ready' | 'error' | 'unavailable'(로그인 안 함)

 잔액은 헤더가 뜰 때·메뉴를 열 때·AI 작업이 끝날 때 다시 읽는다. 조회·견적만으로는 기록이 생기지 않는다.
 닷북 등 다른 서비스도 같은 사용자의 일일 잔액을 쓰므로 들고 있는 값은 그때그때 다시 읽어야 맞다.

 잔액의 canStart 는 "0 보다 많이 남았다"일 뿐이다. AI 이미지 변환은 예상 비용(1회 약 116 크레딧, 2026-11 설정)까지
 남아 있어야 승인되므로 연산마다 견적(estimates)을 함께 읽어 「남았지만 모자람」을 미리 보인다(매뉴얼 03 「GET 잔액 → POST 견적」).
 견적은 승인을 대신하지 않는다 — 실제 허용 여부는 승인·시작 API 가 다시 판단한다.
 */

let seq = 0
// 지난번에 못 보낸 정산은 사용자마다 앱을 연 뒤 처음 읽을 때 한 번 보낸다(같은 값 재전송이라 여러 번이어도 안전하다).
const flushedUsers = new Set()
const AI_OPERATIONS = Object.values(AI_IMAGE_OPERATIONS).map(item => item.operation)

const state = () => ({
    status: "idle",
    balance: null,      // parseCreditBalance 결과
    estimates: {},      // 연산 → parseCreditEstimate 결과(못 읽었으면 null)
    userNo: null,       // 이 잔액의 주인. 다른 계정으로 바뀌면 옛 값을 보이지 않는다.
})

const getters = {
    status: state => state.status,
    balance: state => state.balance,
    estimates: state => state.estimates,
    // 헤더용: AI 이미지 변환(그림·설명 글) 어느 쪽도 1회를 할 만큼 남지 않았다. 견적을 하나도 못 읽었으면 false.
    aiImageShort: state => {
        const known = AI_OPERATIONS.map(operation => state.estimates?.[operation]).filter(Boolean)
        return known.length > 0 && known.every(estimate => isCreditShort(state.balance, estimate))
    },
}

const mutations = {
    setCredit(state, {status, balance, estimates = {}, userNo = state.userNo}) {
        state.status = status
        state.balance = balance
        state.estimates = estimates
        state.userNo = userNo
    },
    setStatus(state, status) {
        state.status = status
    },
    // 승인·시작 API 가 잔액 부족(409)을 돌려줬을 때: 다시 읽기 전이라도 그 연산을 바로 잠근다.
    markInsufficient(state, operation) {
        if (!state.balance || !operation) return
        state.estimates = {...state.estimates, [operation]: {...(state.estimates?.[operation] || {}), canStart: false, reason: "INSUFFICIENT_CREDITS"}}
        state.status = "ready"
    },
}

async function readEstimates(userNo) {
    const entries = await Promise.all(AI_OPERATIONS.map(async operation => {
        try {
            // requestKey 는 문법상 필수일 뿐 견적은 기록을 남기지 않는다.
            const response = await creditApi.estimate(userNo, creditPlan(operation, `canvas:estimate:${operation}`))
            const estimate = response?.status === 200 ? parseCreditEstimate(response.data) : null
            if (!estimate) console.error("[aiCredit] 견적 실패:", operation, response?.status, response?.data)
            return [operation, estimate]
        } catch (e) {
            console.error("[aiCredit] 견적 실패:", operation, e)
            return [operation, null]
        }
    }))
    return Object.fromEntries(entries)
}

const actions = {
    async refresh({commit, state}) {
        const mine = ++seq
        const userNo = $session.getUserNo()
        if (!isCreditUserNo(userNo)) {
            commit("setCredit", {status: "unavailable", balance: null, estimates: {}, userNo: null})
            return
        }
        if (state.userNo !== userNo) commit("setCredit", {status: "loading", balance: null, estimates: {}, userNo})
        else if (!state.balance) commit("setStatus", "loading")
        try {
            if (!flushedUsers.has(userNo)) {
                flushedUsers.add(userNo)
                await flushPendingCreditCalls({api: creditApi, userNo})
            }
            const response = await creditApi.getCredits(userNo)
            if (mine !== seq) return
            const balance = response?.status === 200 ? parseCreditBalance(response.data) : null
            if (!balance) {
                console.error("[aiCredit] 크레딧 조회 실패:", response?.status, response?.data)
                commit("setCredit", {status: "error", balance: null, estimates: {}})
                return
            }
            // 다 썼으면 견적은 볼 것도 없다(어차피 잠근다).
            const estimates = balance.canStart ? await readEstimates(userNo) : {}
            if (mine !== seq) return
            commit("setCredit", {status: "ready", balance, estimates})
        } catch (e) {
            if (mine !== seq) return
            console.error("[aiCredit] 크레딧 조회 실패:", e)
            commit("setCredit", {status: "error", balance: null, estimates: {}})
        }
    },
}

export default {
    namespaced: true,
    state,
    getters,
    mutations,
    actions,
}
